/**
 * The revoked-access guard — the rewrite of GH#117 (charter §16.4 decision 2).
 *
 * Under Firestore, "you were removed from this org" arrived as a *positive
 * event*: the org document listener re-fired with the user absent from
 * `data.users`, and `context_and_index.jsx:487-509` tore the session down.
 * Under RLS there is no such event. The row simply stops being readable, the
 * refetch fails, and the failure looks — at first glance — like every other
 * failure. `docs/supabase-event-architecture.md:348` called this out as "a
 * different code path that must be written and tested, not retargeted". This
 * is that code path.
 *
 * This module imports nothing but the telemetry seam. Not React, not Supabase.
 * Everything it needs is injected, so the provider can wire it in without this
 * file knowing a provider exists, and so every branch is reachable from a test
 * without a live server — which the telemetry import does not change, because it
 * is a no-op until Sentry is initialised.
 *
 * ===========================================================================
 * THE RULE, AND WHY IT IS THIS RULE
 * ===========================================================================
 *
 * Getting this wrong is expensive in both directions:
 *
 *   - Treat a transient failure as revocation → a working session is destroyed
 *     because a train went into a tunnel.
 *   - Treat revocation as transient → a removed user keeps a live, readable
 *     session until they choose to reload.
 *
 * So the rule is deliberately narrow, and it demands a POSITIVE assertion from
 * the server rather than inferring anything from an absence:
 *
 *     Revocation ⟺ SQLSTATE 42501 AND the message carries the `not_a_member:`
 *     sentinel raised by t3.assert_org_member().
 *
 * Both halves are load-bearing. Measured against the live local stack
 * (2026-09-05, supabase/tests/access-guard-live.mjs — every row below is an
 * observation, not a guess):
 *
 *   | what happened                        | code     | message                                    |
 *   |--------------------------------------|----------|--------------------------------------------|
 *   | member calls org_bootstrap           | —        | OK, all slices                             |
 *   | caller is not a member               | 42501    | not_a_member: caller is not a member of …  |
 *   | membership DELETEd mid-session       | 42501    | not_a_member: caller is not a member of …  |
 *   | admin control session ENDED          | 42501    | not_a_member: caller is not a member of …  |
 *   | org id does not exist                | 42501    | not_a_member: caller is not a member of …  |
 *   | NO TOKEN AT ALL (anon)               | 42501    | permission denied for function org_bootstrap |
 *   | malformed token                      | PGRST301 | JWT cryptographic operation failed         |
 *   | tampered signature                   | PGRST301 | No suitable key or wrong key type          |
 *   | EXPIRED token                        | PGRST303 | JWT expired                                |
 *   | server unreachable                   | "" (none)| TypeError: fetch failed                    |
 *   | function missing (deploy skew)       | PGRST202 | Could not find the function …              |
 *   | unreadable row via a table select    | —        | OK, **zero rows, no error at all**         |
 *
 * Three of those rows are the whole reason the sentinel exists:
 *
 * (1) **42501 alone is NOT revocation.** An anonymous caller — a tab whose
 *     Firebase token has not loaded yet, or a sign-out race — also gets 42501,
 *     because `anon` holds no EXECUTE grant on the function. Classifying on the
 *     SQLSTATE alone would tear down a session during ordinary token churn.
 *     This is the single most likely wrong implementation of this module, and
 *     it is wrong in the dangerous direction: it fires on healthy sessions.
 *
 * (2) **An expired token is PGRST303, not 42501.** A laptop that slept for two
 *     hours wakes with a stale JWT. Firebase refreshes it; the correct response
 *     is to retry, not to end the session. Kept strictly separate.
 *
 * (3) **Silence is not evidence.** An RLS-filtered *table* read of a row you
 *     may no longer see returns `[]` with no error — indistinguishable from a
 *     legitimately empty result. This module therefore NEVER concludes
 *     revocation from an empty read. `confirm()` exists for exactly that case:
 *     it escalates the ambiguity to the one caller-gated RPC that answers the
 *     question authoritatively.
 *
 * WHY A SINGLE HIT IS ENOUGH (no retry, no debounce). The sentinel is raised by
 * `t3.assert_org_member()` inside a SECURITY DEFINER function after the server
 * evaluated `t3.user_org_ids()` and `t3.admin_org_ids()` against live rows. No
 * network condition can synthesise it: a blip yields no code, a proxy yields
 * 5xx, an expired token yields PGRST303. It is a server-computed fact about
 * membership, so acting on the first one is correct and delay is pure exposure.
 *
 * NOTE ON A DELETED ORG. A stale `lastUsedOrgId` pointing at an org that no
 * longer exists returns the same sentinel — the gate cannot distinguish "gone"
 * from "not yours", by design (leaking which org ids exist is the thing it is
 * avoiding). The verdict is the same one the user needs either way: you cannot
 * be in this org, go pick another. That matches the Firestore behaviour this
 * replaces, whose comment likewise listed "a wrong lastUsedOrgId" alongside
 * revocation.
 *
 * ===========================================================================
 * THE ADMIN-CONTROL-SESSION CASE
 * ===========================================================================
 *
 * `t3.assert_org_member()` passes on EITHER of two facts:
 *
 *     p_org_id = ANY (t3.user_org_ids())    -- your own membership row
 *  OR p_org_id = ANY (t3.admin_org_ids())   -- an OPEN take-control session
 *
 * (docs/proposed-schema.sql:4714-4720; `t3.admin_org_ids()` at :18874 reads
 * `admin_control_sessions WHERE user_id = t3.uid() AND ended_at IS NULL`.)
 *
 * So when a platform admin's take-control session ends, `admin_org_ids()` stops
 * returning that org and the next call raises the identical error a revoked
 * member gets. MEASURED: with the session open, org_bootstrap returned every
 * slice; one `UPDATE … SET ended_at = now()` later, byte-identical 42501
 * `not_a_member:` — the same string, in the same field, as a genuine DELETE
 * from org_members.
 *
 * The error therefore CANNOT carry this distinction, and no amount of parsing
 * will recover it. It has to come from the client's own knowledge of how this
 * session reached the org — which the client has, because admin control
 * sessions ride the `user:{uid}` channel (charter §16.4). Hence
 * `getAccessBasis`: the caller says whether it is here as a member or as a
 * controlling admin, and the guard routes the same signal to a different
 * callback. An ended control session is a normal, expected end of a support
 * visit; it should return the admin to their console, not raise a security
 * alarm or imply their own account lost access.
 *
 * When the basis is unknown the guard fails CLOSED — it reports revocation.
 * Both verdicts tear the org-scoped session down; the basis only decides which
 * story the user is told, so the secure default costs nothing but a wrong
 * message in a case that should not arise.
 */
import { dataBreadcrumb } from "./telemetry.js";

/** Verdicts. Exported so callers and tests name the same things. */
export const OK = "ok";
export const REVOKED = "revoked";
export const CONTROL_ENDED = "control-ended";
export const UNAUTHENTICATED = "unauthenticated";
export const TRANSIENT = "transient";
export const UNSUPPORTED = "unsupported";

/** Access bases, as reported by the caller for the org in question. */
export const BASIS_MEMBER = "member";
export const BASIS_CONTROL = "control";

/**
 * The sentinel `t3.assert_org_member()` raises. Anchored at the start and
 * followed by a colon so it cannot be matched by user-supplied text that merely
 * contains the phrase. The optional prefix covers the AP/AR gate's
 * `ap_ar_not_a_member:` (docs/proposed-schema.sql:8156), which is the same
 * assertion under a different name.
 */
const NOT_A_MEMBER = /^[a-z0-9_]*not_a_member:/;

/**
 * PostgREST's JWT-verification failures. Both PGRST301 (malformed token, bad
 * signature, unknown key) and PGRST303 (expiry) were observed; PGRST302
 * ("anonymous access disabled") is included on the same reasoning but was NOT
 * measured here — said plainly so nobody reads it as evidence. All three mean
 * "this request had no usable identity", an auth-layer problem the token
 * refresh fixes, and none says anything about membership.
 */
const JWT_CODES = new Set(["PGRST301", "PGRST302", "PGRST303"]);

/**
 * PostgREST codes that mean the *request* was wrong: a function that is not in
 * the schema cache (deploy skew), a shape mismatch. Never an access fact, and
 * never worth retrying, so they are separated from TRANSIENT rather than folded
 * into it — a retry loop against PGRST202 is an infinite one.
 */
const UNSUPPORTED_CODES = new Set(["PGRST202", "PGRST203", "PGRST204"]);

const messageOf = (error) =>
  typeof error?.message === "string" ? error.message : String(error?.message ?? "");

/**
 * Classify one failure. Pure — no state, no side effects, safe to call on
 * anything including `null`.
 *
 * @param {{code?: string, message?: string} | Error | null} error
 * @param {{basis?: string}} [ctx]  How this session reached the org. See the
 *   admin-control-session note above; omit it and revocation is assumed.
 * @returns {{verdict: string, reason: string, code: string|null}}
 */
export function classifyAccessError(error, { basis } = {}) {
  if (!error) return { verdict: OK, reason: "no error", code: null };

  const code = error.code ?? null;
  const message = messageOf(error);

  // The one positive assertion. Both halves required — see (1) above.
  if (code === "42501" && NOT_A_MEMBER.test(message)) {
    return basis === BASIS_CONTROL
      ? {
          verdict: CONTROL_ENDED,
          reason: "membership gate refused and this session held the org by an admin control session",
          code,
        }
      : { verdict: REVOKED, reason: "membership gate refused (not_a_member)", code };
  }

  // A 42501 WITHOUT the sentinel is a grant problem, not a membership one. The
  // observed shape is anon calling a function it may not EXECUTE, i.e. the
  // request carried no usable identity — same remedy as an expired token.
  if (code === "42501") {
    return { verdict: UNAUTHENTICATED, reason: `permission denied without membership sentinel: ${message}`, code };
  }

  if (JWT_CODES.has(code)) {
    return { verdict: UNAUTHENTICATED, reason: `token rejected: ${message}`, code };
  }

  if (UNSUPPORTED_CODES.has(code)) {
    return { verdict: UNSUPPORTED, reason: `request not served: ${message}`, code };
  }

  // Everything else: a dropped socket (supabase-js reports these with an empty
  // `code` and a `TypeError: fetch failed` message), a 5xx, a statement
  // timeout. Unknown failures land here on purpose — the safe default for
  // something we cannot name is "retry", not "end the session".
  return { verdict: TRANSIENT, reason: message || "unclassified failure", code };
}

/**
 * The stateful guard the provider wires in.
 *
 * @param {object} opts
 * @param {(orgId: string) => Promise<unknown>} [opts.probeAccess]
 *   The authoritative membership probe — in production, the `org_bootstrap`
 *   RPC (src/data/useOrgRefdata.js `probeOrgAccess`). Only used by `confirm()`,
 *   for the ambiguous silent case. Omit it and `confirm()` returns UNSUPPORTED
 *   rather than OK: a guard that clears a session on a probe that never ran is
 *   worse than no guard.
 * @param {(orgId: string) => string|undefined} [opts.getAccessBasis]
 *   Returns BASIS_MEMBER or BASIS_CONTROL for the org. Backed by the caller's
 *   own view of admin control sessions (the `user:{uid}` channel).
 * @param {(info: {orgId: string, reason: string, code: string|null}) => void} [opts.onAccessRevoked]
 *   Fired at most once per opened org. THE contract the provider implements:
 *   tear the org-scoped session down.
 * @param {(info: {orgId: string, reason: string, code: string|null}) => void} [opts.onControlSessionEnded]
 *   Same teardown, different story — a support visit ended normally. Falls back
 *   to `onAccessRevoked` when not supplied, so a caller that does not care about
 *   the distinction still fails closed.
 * @param {(info: {orgId: string, verdict: string, reason: string, code: string|null}) => void} [opts.onTransient]
 *   Everything the guard decided NOT to act on. Optional, and purely
 *   observational — its value is that a misclassification is visible in logs
 *   instead of silent.
 */
export function createAccessGuard({
  probeAccess,
  getAccessBasis,
  onAccessRevoked,
  onControlSessionEnded,
  onTransient,
} = {}) {
  let orgId = null;
  let fired = false;

  const basisFor = (id) => {
    try {
      return getAccessBasis?.(id);
    } catch (error) {
      // A throwing basis lookup must not decide the security question. Unknown
      // basis fails closed to REVOKED — behaviour unchanged, deliberately.
      //
      // But that swallow silently converts a CONTROL_ENDED into a REVOKED, so a
      // platform admin whose take-control session simply expired is told they
      // lost access to the customer's org. A breadcrumb rather than an event:
      // the fallback is correct and not itself an incident; what it costs is the
      // ability to answer "did the guard really classify revocation, or did the
      // basis lookup throw?" on the teardown event that follows it — and a
      // breadcrumb rides along on exactly that event.
      dataBreadcrumb("access", "basis lookup threw — failing closed", {
        org_id: id,
        error: error?.message ?? String(error),
      });
      return undefined;
    }
  };

  const emit = (result) => {
    if (result.verdict === REVOKED || result.verdict === CONTROL_ENDED) {
      // Once per open org. A failed bootstrap typically fans out into several
      // rejected calls; the provider must tear down once, not once per call.
      if (fired) return result;
      fired = true;
      const info = { orgId, reason: result.reason, code: result.code };
      if (result.verdict === CONTROL_ENDED && onControlSessionEnded) onControlSessionEnded(info);
      else onAccessRevoked?.(info);
      return result;
    }
    if (result.verdict !== OK) onTransient?.({ orgId, ...result });
    return result;
  };

  return {
    get orgId() { return orgId; },
    /** True once a teardown has been reported for the currently open org. */
    get revoked() { return fired; },

    /** Point the guard at an org. Re-arms if the org changed. */
    open(nextOrgId) {
      if (orgId !== nextOrgId) fired = false;
      orgId = nextOrgId;
    },

    close() { orgId = null; fired = false; },

    /**
     * Classify a failure from any org-scoped call and act on it.
     *
     * `forOrg` is not optional ceremony. An org switch leaves the previous
     * org's in-flight requests to reject afterwards; without the tag, a
     * perfectly correct `not_a_member` for the org the user just LEFT would
     * tear down the org they just arrived at. Verdicts for any other org are
     * classified and returned but never acted on.
     */
    report(error, { forOrg = orgId } = {}) {
      const result = classifyAccessError(error, { basis: basisFor(forOrg) });
      if (!orgId || forOrg !== orgId) return result;
      return emit(result);
    },

    /**
     * Resolve the ambiguous case: something came back empty and we cannot tell
     * whether the rows are gone or merely unreadable. An RLS-filtered table
     * read yields `[]` with no error (measured), so there is nothing to
     * classify — the only way to get an answer is to ask the caller-gated RPC,
     * whose refusal IS the answer.
     *
     * Returns the verdict. A probe that succeeds returns OK, which is the
     * useful half: it positively clears a session that looked suspicious.
     *
     * WITH NO PROBE WIRED THIS ANSWERS NOTHING, and says so. `await undefined`
     * resolves, so an optional-call here would return OK — "this session is
     * fine" for a question nobody asked — and a caller reading that as
     * clearance is worse off than one with no guard at all, because the OK
     * reads as evidence. UNSUPPORTED is the honest verdict and it is inert:
     * `emit` acts only on REVOKED/CONTROL_ENDED, so an unwired guard reports
     * through `onTransient` and tears nothing down.
     */
    async confirm() {
      if (!orgId) return { verdict: OK, reason: "no org open", code: null };
      if (!probeAccess) return emit({ verdict: UNSUPPORTED, reason: "no probe wired", code: null });
      const probedOrg = orgId;
      try {
        await probeAccess(probedOrg);
        return { verdict: OK, reason: "probe succeeded", code: null };
      } catch (error) {
        // supabase-js resolves rather than rejects; a caller wrapping it is
        // expected to throw the PostgrestError. Both shapes classify the same.
        const result = classifyAccessError(error, { basis: basisFor(probedOrg) });
        if (probedOrg !== orgId) return result;   // org switched mid-probe
        return emit(result);
      }
    },
  };
}

export default createAccessGuard;
